import SeoMeta from "@/components/SeoMeta";

const VERIFIED_BADGE_URL = "https://localbest.ai/assets/localbest-verified-badge.png";
const BASE_URL = "https://localbest.ai";

// og:image REQUIRES an absolute URL. If the field holds a relative path, this
// prepends BASE_URL so social platforms can fetch it.
function absolutize(url, baseUrl = BASE_URL) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${baseUrl.replace(/\/$/, "")}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
}

// Share image fallback chain (best → last-resort):
//   1. og_image           — owner explicitly overrode the share image
//   2. background_image_url — the listing hero cover (real product/team photo)
//   3. logo_url           — brand mark, still recognizable
//   4. Verified badge     — universal LocalBest look for listings with no imagery
function buildOgImageUrl(business, baseUrl = BASE_URL) {
  return (
    absolutize(business?.og_image, baseUrl) ||
    absolutize(business?.background_image_url, baseUrl) ||
    absolutize(business?.logo_url, baseUrl) ||
    VERIFIED_BADGE_URL
  );
}

// Share headline — predictable, the same shape on every listing:
//   "{Business / Agent Name} on The AI Horse-Powered Best of Best Directory™"
function buildShareTitle(business) {
  if (business?.og_title) return business.og_title;
  const name = business?.name || "LocalBest Business";
  return `${name} on The AI Horse-Powered Best of Best Directory\u2122`;
}

// No description by default — empty string so social platforms don't render a
// description line (cleaner card). Owner can still override.
function buildShareDescription(business) {
  if (business?.og_description) return business.og_description;
  return "";
}

export function SEOHead({ business, baseUrl = BASE_URL }) {
  if (!business) return null;

  const location = [business.city, business.state].filter(Boolean).join(", ");

  // SEO title (long, descriptive — used for <title> and search engines)
  const seoTitle = business.seo_title ||
    `${business.name} – ${business.category_name} in ${location || "Your Area"} | LocalBest.ai`;

  // SHARE title (short, predictable — used for og:title / twitter:title)
  const shareTitle = buildShareTitle(business);
  // SHARE description — empty by default per product spec (no description line on the card)
  const shareDescription = buildShareDescription(business);
  // SEO description (browsers / Google) — keep informative for search engines
  const metaDescription = business.meta_description ||
    business.seo_description ||
    `Trusted ${(business.category_name || "business").toLowerCase()} in ${location || "your area"}. View contact info, services, and reviews for ${business.name}.`;

  const ogImage = buildOgImageUrl(business, baseUrl);
  // Alt text so the share card is accessible + Twitter renders a caption
  const ogImageAlt = business?.og_image_alt ||
    `${business.name}${business.category_name ? ` — ${business.category_name}` : ""}${location ? ` in ${location}` : ""} · LocalBest.ai`;

  // Canonical URL — always clean, no tracking params
  const citySlug = (business.city || "local").toLowerCase().replace(/\s+/g, "-");
  const stateSlug = (business.state || "").toLowerCase();
  const cityState = stateSlug ? `${citySlug}-${stateSlug}` : citySlug;
  const canonicalUrl = `${baseUrl}/directory/${business.category_id || "business"}/${cityState}/${business.slug}`;

  // LocalBusiness schema
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": canonicalUrl,
    "name": business.name,
    "description": business.seo_description || business.description,
    "url": canonicalUrl,
    "telephone": business.contact_phone || undefined,
    "email": business.contact_email || undefined,
    "image": business.logo_url || ogImage,
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": business.street_address || business.address || undefined,
      "addressLocality": business.city || undefined,
      "addressRegion": business.state || undefined,
      "postalCode": business.zip_code || undefined,
      "addressCountry": business.country || "USA"
    },
    ...(business.latitude && business.longitude ? {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": business.latitude,
        "longitude": business.longitude
      }
    } : {}),
    ...(business.hours ? {
      "openingHoursSpecification": Object.entries(business.hours)
        .filter(([, hours]) => hours && typeof hours === 'string' && hours.includes(' - '))
        .map(([day, hours]) => ({
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": day.charAt(0).toUpperCase() + day.slice(1),
          "opens": hours.split(" - ")[0] || "09:00",
          "closes": hours.split(" - ")[1] || "17:00"
        }))
    } : {}),
    ...(business.rating && business.review_count > 0 ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": business.rating,
        "reviewCount": business.review_count,
        "bestRating": 5,
        "worstRating": 1
      }
    } : {}),
    ...(business.social_links ? {
      "sameAs": Object.values(business.social_links).filter(Boolean)
    } : {}),
    ...(business.services?.length > 0 ? {
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Services",
        "itemListElement": business.services.map((service) => ({
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": service }
        }))
      }
    } : {}),
    ...(business.is_service_area_business && business.service_areas?.length > 0 ? {
      "areaServed": business.service_areas.map(area => ({
        "@type": "City", "name": area
      }))
    } : {})
  };

  const cleanJsonLd = JSON.parse(JSON.stringify(jsonLd));

  // Breadcrumb schema
  const breadcrumbItems = [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
    { "@type": "ListItem", "position": 2, "name": "Directory", "item": `${baseUrl}/directory` },
  ];
  if (business.category_name) {
    breadcrumbItems.push({
      "@type": "ListItem", "position": 3,
      "name": business.category_name,
      "item": `${baseUrl}/directory/${business.category_id || "business"}`
    });
  }
  breadcrumbItems.push({
    "@type": "ListItem",
    "position": breadcrumbItems.length + 1,
    "name": business.name,
  });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems,
  };

  // FAQ schema
  const faqSchema = business.faq?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": business.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  } : null;

  const ldBlocks = [cleanJsonLd, breadcrumbSchema];
  if (faqSchema) ldBlocks.push(faqSchema);

  const extra = [
    { name: "robots", content: "index, follow" },
    { property: "og:image:alt", content: ogImageAlt },
    { name: "twitter:image:alt", content: ogImageAlt },
  ];
  if (business.keywords?.length > 0) {
    extra.push({ name: "keywords", content: business.keywords.join(", ") });
  }
  if (business.city) extra.push({ property: "business:contact_data:locality", content: business.city });
  if (business.state) extra.push({ property: "business:contact_data:region", content: business.state });
  if (business.country) extra.push({ property: "business:contact_data:country_name", content: business.country });
  if (business.latitude && business.longitude) {
    extra.push({ name: "geo.position", content: `${business.latitude};${business.longitude}` });
    extra.push({ name: "ICBM", content: `${business.latitude}, ${business.longitude}` });
  }
  if (location) extra.push({ name: "geo.placename", content: location });
  if (business.state) extra.push({ name: "geo.region", content: `US-${business.state}` });

  return (
    <SeoMeta
      title={seoTitle}
      description={metaDescription}
      canonical={canonicalUrl}
      ogTitle={shareTitle}
      ogDescription={shareDescription || null}
      ogImage={ogImage}
      ogType="business.business"
      ogUrl={canonicalUrl}
      ogSiteName="LocalBest.ai — The AI Horse-Powered Best of Best Directory™"
      twitterCard="summary_large_image"
      twitterTitle={shareTitle}
      twitterDescription={shareDescription || null}
      twitterImage={ogImage}
      extraMeta={extra}
      jsonLd={ldBlocks}
    />
  );
}

export default SEOHead;
